import React from 'react';

interface ZipCodeSelectionFormProps {
  onChange: (zip: string) => void;
  onSubmit: () => void;
  zipCode: string;
}

/**
 * ZipCodeSelectionForm renders a zipCode input. It is meant to be rendered inside another
 * component that handles the zipCode submission logic. The consuming component should use
 * `onSubmit`, `zipCode`, and `onChange` from the `useZipCodeInput` hook.

 * @param onChange - A callback function that is called when the zipCode input changes
 * @param onSubmit - A callback function that is called when the form is submitted
 * @param zipCode - The current value of the zipCode input
 * @returns
 */
export const ZipCodeSelectionForm: React.FC<ZipCodeSelectionFormProps> = ({ onChange, onSubmit, zipCode }) => {
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleEnterForm = () => {
    if (isReadOnly) {
      setIsReadOnly(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        await onSubmit();
      }}
      className='flex w-full items-center gap-2 rounded-xl border border-solid border-neutral-200 bg-white p-2 text-neutral-400'>
      <input
        ref={inputRef}
        className='m-0 w-full p-0 text-xl leading-none text-neutral-800 outline-none focus:outline-none active:outline-none'
        inputMode='numeric'
        pattern='[0-9]*'
        placeholder='Zip code'
        readOnly={isReadOnly}
        type='text'
        value={zipCode}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        onClick={handleEnterForm}
        onTouchEnd={handleEnterForm}
      />
    </form>
  );
};
